import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';
import { formatCurrency, formatDate } from '../../utils/formatters.js';

const STATUS_VARIANT = { DRAFT: 'neutral', SUBMITTED: 'warning', APPROVED: 'info', REJECTED: 'danger', REIMBURSED: 'success' };

export function TravelReportDetailModal({ reportId, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);

  const detail = useQuery({ queryKey: ['hr', 'travel-report', reportId], queryFn: () => hrApi.getTravelReport(reportId).then((r) => r.items), enabled: Boolean(reportId) });
  const report = detail.data;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['hr', 'travel-reports'] });
    queryClient.invalidateQueries({ queryKey: ['hr', 'travel-report', reportId] });
  };
  const run = (fn) => ({ mutationFn: fn, onSuccess: () => { setError(null); invalidate(); }, onError: (e) => setError(e?.message || t('common.loadError')) });
  const submit = useMutation(run(() => hrApi.submitTravelReport(reportId)));
  const approve = useMutation(run(() => hrApi.approveTravelReport(reportId)));
  const reject = useMutation(run(() => hrApi.rejectTravelReport(reportId)));
  const reimburse = useMutation(run(() => hrApi.reimburseTravelReport(reportId)));
  const busy = submit.isPending || approve.isPending || reject.isPending || reimburse.isPending;

  return (
    <Modal
      open={Boolean(reportId)}
      onClose={onClose}
      title={report ? `${report.reportNumber} · ${report.purpose}` : t('tr.detail.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>
          {report?.status === 'DRAFT' && <Button variant="primary" disabled={busy} onClick={() => submit.mutate()}>{t('tr.action.submit')}</Button>}
          {report?.status === 'SUBMITTED' && <Button variant="ghost" disabled={busy} onClick={() => reject.mutate()}>{t('tr.action.reject')}</Button>}
          {report?.status === 'SUBMITTED' && <Button variant="primary" disabled={busy} onClick={() => approve.mutate()}>{t('tr.action.approve')}</Button>}
          {report?.status === 'APPROVED' && <Button variant="primary" disabled={busy} onClick={() => reimburse.mutate()}>{t('tr.action.reimburse')}</Button>}
        </>
      }
    >
      {report && (
        <div className="form-stack">
          <p className="muted text-sm" style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            <Badge variant={STATUS_VARIANT[report.status]}>{t(`tr.status.${report.status}`)}</Badge>
            {report.employeeName}
            {report.fromDate ? ` · ${formatDate(report.fromDate)}-${report.toDate ? formatDate(report.toDate) : ''}` : ''}
          </p>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>{t('tr.col.category')}</th><th>{t('tr.field.description')}</th><th className="num">{t('tr.field.amount')}</th></tr>
              </thead>
              <tbody>
                {(report.items || []).map((it) => (
                  <tr key={it.id}>
                    <td>{t(`tr.cat.${it.category}`)}</td>
                    <td>{it.description || '-'}</td>
                    <td className="num mono">{formatCurrency(it.amount)}</td>
                  </tr>
                ))}
                <tr><td colSpan={2} className="num"><strong>{t('tr.col.total')}</strong></td><td className="num mono"><strong>{formatCurrency(report.total)}</strong></td></tr>
              </tbody>
            </table>
          </div>
          {error && <div className="alert danger">{error}</div>}
        </div>
      )}
    </Modal>
  );
}
